import { APP_CONFIG } from '@/lib/app/config';
import { useAuth } from '@/lib/auth/auth';
import { trackLocationPermission } from '@/lib/analytics/posthog';
import { captureSentryException } from '@/lib/analytics/sentry';
import { canSyncPreciseLocation } from '@/lib/location/locationPrivacy';
import * as ExpoLocation from 'expo-location';
import * as React from 'react';
import { AppState, Platform } from 'react-native';
import { create } from 'zustand';

const LOCATION_RESOLUTION_TIMEOUT_MS = 10_000;

export type Coords = {
  latitude: number;
  longitude: number;
};

type LocationState = {
  coords: Coords;
  isReady: boolean;
  permissionDenied: boolean;
  awaitingPermissionChoice: boolean;
  resolvedAt: number;
  resolve: (options?: { requestPermission?: boolean }) => Promise<void>;
};

export const locationStore = create<LocationState>((set) => ({
  coords: APP_CONFIG.defaultLocation,
  isReady: false,
  permissionDenied: false,
  awaitingPermissionChoice: false,
  resolvedAt: 0,
  async resolve(options = {}) {
    try {
      const currentPermission = await ExpoLocation.getForegroundPermissionsAsync();
      const needsPrompt = currentPermission.status === ExpoLocation.PermissionStatus.UNDETERMINED;
      const shouldPrompt = options.requestPermission !== false && needsPrompt;

      if (needsPrompt && !shouldPrompt) {
        set({
          isReady: true,
          permissionDenied: true,
          awaitingPermissionChoice: false,
          resolvedAt: Date.now(),
        });
        return;
      }

      if (shouldPrompt) {
        set({ awaitingPermissionChoice: true });
      }

      const nextPermission = shouldPrompt
        ? await ExpoLocation.requestForegroundPermissionsAsync()
        : currentPermission;

      if (nextPermission.status !== 'granted') {
        if (shouldPrompt) {
          trackLocationPermission({
            granted: false,
            source: 'location_onboarding',
          });
        }
        set({
          isReady: true,
          permissionDenied: true,
          awaitingPermissionChoice: false,
          resolvedAt: Date.now(),
        });
        return;
      }

      if (shouldPrompt) {
        trackLocationPermission({
          granted: true,
          source: 'location_onboarding',
        });
      }
      const loc = await getCurrentPosition();
      set({
        coords: {
          latitude: loc.coords.latitude,
          longitude: loc.coords.longitude,
        },
        isReady: true,
        permissionDenied: false,
        awaitingPermissionChoice: false,
        resolvedAt: Date.now(),
      });
    } catch (error) {
      if (Platform.OS !== 'web') {
        captureSentryException(error, {
          tags: { area: 'location', kind: 'resolution' },
        });
      }
      set({
        isReady: true,
        permissionDenied: true,
        awaitingPermissionChoice: false,
        resolvedAt: Date.now(),
      });
    }
  },
}));

async function getCurrentPosition() {
  let timeout: ReturnType<typeof setTimeout> | undefined;

  try {
    return await Promise.race([
      ExpoLocation.getCurrentPositionAsync({ accuracy: ExpoLocation.Accuracy.Balanced }),
      new Promise<never>((_, reject) => {
        timeout = setTimeout(
          () => reject(new Error('Location resolution timed out')),
          LOCATION_RESOLUTION_TIMEOUT_MS
        );
      }),
    ]);
  } finally {
    if (timeout) {
      clearTimeout(timeout);
    }
  }
}

export function LocationProvider({ children }: { children: React.ReactNode }) {
  const resolve = locationStore((state) => state.resolve);
  const coords = locationStore((state) => state.coords);
  const isReady = locationStore((state) => state.isReady);
  const permissionDenied = locationStore((state) => state.permissionDenied);
  const resolvedAt = locationStore((state) => state.resolvedAt);
  const userID = useAuth((state) => state.session?.me.user.id ?? null);
  const locationTrackingEnabled = useAuth(
    (state) => canSyncPreciseLocation(state.session?.me.profile ?? {})
  );
  const syncMarketLocation = useAuth((state) => state.syncMarketLocation);
  const appStateRef = React.useRef(AppState.currentState);

  React.useEffect(() => {
    resolve({ requestPermission: false }).catch(() => undefined);
  }, [resolve]);

  React.useEffect(() => {
    const subscription = AppState.addEventListener('change', (nextState) => {
      const previousState = appStateRef.current;
      appStateRef.current = nextState;

      if (nextState === 'active' && previousState === 'background') {
        resolve({ requestPermission: false }).catch(() => undefined);
      }
    });

    return () => subscription.remove();
  }, [resolve]);

  React.useEffect(() => {
    if (!userID || !locationTrackingEnabled || !isReady || permissionDenied || resolvedAt === 0) {
      return;
    }

    syncMarketLocation(coords.latitude, coords.longitude).catch(() => undefined);
  }, [
    coords.latitude,
    coords.longitude,
    isReady,
    permissionDenied,
    locationTrackingEnabled,
    resolvedAt,
    syncMarketLocation,
    userID,
  ]);

  return React.createElement(React.Fragment, null, children);
}

export function useLocation() {
  return locationStore();
}
